// order matters: reset first, then fonts, then tailwind (global.css) so utilities win over reset
import "./assets/styles/reset.css"
import "@productlane/ui/src/styles/shared.css"
import "@productlane/ui/src/styles/mintlify/index.css"
import "highlight.js/styles/github-dark.css"
import "./assets/font-files/inter.css"
import "./assets/styles/global.css"
import "./env"

import * as Sentry from "@sentry/react"
import { QueryClientProvider } from "@tanstack/react-query"
import { RouterProvider } from "@tanstack/react-router"
import ReactDOM from "react-dom/client"
import { Provider as WrapProvider } from "react-wrap-balancer"

import { TooltipProvider } from "@productlane/ui/src/components/internal/tooltip"
import { installAutofillGuard } from "@productlane/ui/src/lib/autofill-guard"

import { ErrorBoundary } from "./components/error-boundary"
import { FirstRunReveal } from "./components/first-run-reveal"
import { Toaster } from "./components/toast"
import { UndoContextProvider } from "./context/undo-context"
import { env, isDevEnvironment, isProductionEnvironment } from "./env"
import { PosthogProvider } from "./lib/analytics"
import { api, apiClient, reactQueryClient } from "./lib/api"
import { loadGoogleAdsTag } from "./lib/google-ads"
import {
	isAbortedRequest,
	isBrowserNetworkFailure,
	isBrowserStorageFailure,
	isLinkScannerRejection,
	isZeroConnectionLoss,
} from "./lib/ignored-sentry-errors"
import {
	installStaleChunkReload,
	isReloadingForStaleChunk,
} from "./lib/stale-chunk-reload"
import { installTranslationDomGuard } from "./lib/translation-dom-guard"
import { ensureWorkingWebSocket } from "./lib/websocket-guard"
import { isChangelogNotificationAllowed } from "./lib/widget-changelog-gate"
import { createAppRouter } from "./router"
import { SnoozeDialogHost } from "./screens/app/threads/components/center-content/snooze-dialog"

if (isProductionEnvironment) {
	const w = window as unknown as { dataLayer?: unknown[] }
	w.dataLayer = w.dataLayer || []
	w.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" })
	const script = document.createElement("script")
	script.async = true
	script.src = "https://www.googletagmanager.com/gtm.js?id=GTM-52W7QFWC"
	document.head.appendChild(script)

	loadGoogleAdsTag()
}

const router = createAppRouter()

Sentry.init({
	dsn: env.VITE_SENTRY_DSN,
	environment: env.VITE_APP_ENV ?? import.meta.env.MODE,
	integrations: [
		Sentry.tanstackRouterBrowserTracingIntegration(router),
		Sentry.replayIntegration(),
	],
	tunnel: `${env.VITE_API_URL}/api/sentry`,
	// Capture 100% of transactions for performance monitoring
	tracesSampleRate: 1.0,
	// Only enable distributed tracing for these URLs
	tracePropagationTargets: ["localhost", /^https:\/\/productlane\.com\/api/],
	// Capture Replay for 0% of normal sessions, but 100% of sessions with errors
	replaysSessionSampleRate: 0,
	replaysOnErrorSampleRate: 1.0,
	beforeSend: (event) =>
		isReloadingForStaleChunk() ||
		isBrowserNetworkFailure(event) ||
		isBrowserStorageFailure(event) ||
		isZeroConnectionLoss(event) ||
		isLinkScannerRejection(event) ||
		isAbortedRequest(event)
			? null
			: event,
})

// Protect React DOM operations from nodes moved by translation tools.
installTranslationDomGuard()

installStaleChunkReload()

// Some browser extensions clobber window.WebSocket; restore a working constructor
// before Zero opens its sync socket.
ensureWorkingWebSocket()

void (async () => {
	try {
		const controller = new AbortController()
		const timer = setTimeout(() => controller.abort(), 3000)
		await fetch(`${env.VITE_API_URL}/ingest/decide/`, {
			method: "GET",
			signal: controller.signal,
			credentials: "omit",
		})
		clearTimeout(timer)
		Sentry.setTag("adblocker_detected", "false")
	} catch {
		Sentry.setTag("adblocker_detected", "true")
	}
})()

// Only the /login email field may offer saved emails and passwords.
installAutofillGuard()

ReactDOM.createRoot(document.getElementById("root")!).render(
	<ErrorBoundary boundary="root">
		<api.Provider client={apiClient} queryClient={reactQueryClient}>
			<QueryClientProvider client={reactQueryClient}>
				<PosthogProvider>
					<WrapProvider>
						<TooltipProvider>
							<UndoContextProvider>
								{/* Outside the router: the veil has to cover the boot, not appear after it. */}
								<FirstRunReveal />
								<RouterProvider router={router} />
								<Toaster />
								<SnoozeDialogHost />
							</UndoContextProvider>
						</TooltipProvider>
					</WrapProvider>
				</PosthogProvider>
			</QueryClientProvider>
		</api.Provider>
	</ErrorBoundary>,
)

// With the SMALL launcher's 6px pad this gives 8px to the left and bottom edges,
// so the button sits square in the rail's corner.
const WIDGET_RAIL_OFFSET = { bottom: "2px", left: "2px" } as const

//* Initialize Productlane widget if in development environment
if (isDevEnvironment) {
	// @ts-expect-error Global widget calls
	// eslint-disable-next-line
	Productlane.init({
		widgetKey: env.VITE_PRODUCTLANE_WIDGET_KEY,

		theme: {
			"--accent-color": "#FF932E",
			"--accent-color-dark": "#47FC3E",
		},
		mode: "auto",
		position: "left",
		size: "SMALL",
		offset: WIDGET_RAIL_OFFSET,
		disableChangelogNotification: !isChangelogNotificationAllowed(),
	})
	import("@productlane/widget-orchestrator/dist/latest.productlane-widget.min.js").catch(
		(err) => {
			console.error("Failed to load local widget orchestrator", err)
		},
	)
} else {
	//! Initialize Productlane widget in production environment
	// @ts-expect-error Global widget calls
	// eslint-disable-next-line
	Productlane.init({
		widgetKey: env.VITE_PRODUCTLANE_WIDGET_KEY, //! Productlane widget key, do not change
		position: "left",
		size: "SMALL",
		offset: WIDGET_RAIL_OFFSET,
		mode: "auto",
		disableChangelogNotification: !isChangelogNotificationAllowed(),
	})
	const script = document.createElement("script")
	script.setAttribute("async", "true")
	script.setAttribute("defer", "true")
	script.setAttribute("crossorigin", "anonymous")
	script.setAttribute(
		"src",
		`${env.VITE_WIDGET_ORCHESTRATION_URL}latest.productlane-widget.min.js`,
	)
	document.body.appendChild(script)
}
